import type { CaptureResult, Properties } from 'posthog-js';
import { getBasename, resolveRoutePatternForPath } from './taxonomy';

/**
 * Backing scrub for posthog-js's `before_send` hook (wired up in
 * posthog.ts via scrubCaptureResult() below).
 *
 * NOTE ON `sanitize_properties` vs `before_send`: an earlier version of
 * this fix used posthog-js's `sanitize_properties` config option. That
 * hook is `@deprecated` in the installed SDK, and — more importantly —
 * every single call logs `console.error('sanitize_properties is
 * deprecated. Use before_send instead')`, i.e. once per captured event.
 * Sentry's consoleLoggingIntegration ships `console.error` as log lines in
 * prod/staging, so that would have turned every analytics event into a
 * Sentry log line. `before_send` (see scrubCaptureResult()) has no such
 * side effect and is the supported hook, so this uses that instead — see
 * posthog.ts.
 *
 * Plan 009 scrubs `$current_url` on our own `$pageview` capture (see
 * SentryRoute.tsx), but posthog-js stamps several MORE URL-bearing
 * properties on every event, entirely outside our control — and, because
 * plan 010 queues events for later flush, it stamps them at FLUSH time, so
 * even a same-request override would be stale. Observed leaking a company
 * slug via `$pathname` in preview QA (BIL-4824 follow-up). Per
 * docs/product-analytics-contract.md, no event property may ever carry a
 * raw pathname, a URL with company/project slugs, or an id.
 *
 * Properties confirmed in the installed posthog-js build
 * (node_modules/posthog-js/dist/module.js) that carry a path or full URL:
 *  - top level, every event: `$current_url`, `$pathname`, `$referrer`,
 *    `$session_entry_url`, `$session_entry_pathname`,
 *    `$session_entry_referrer`, `$prev_pageview_pathname`
 *  - inside the `$set_once` bag (identify / person properties):
 *    `$initial_current_url`, `$initial_pathname`, `$initial_referrer`
 * (`$referring_domain` and `$host` are left alone — domain-only, no path.)
 *
 * Rather than hand-maintain that list (and miss the next one posthog-js
 * adds), this scrubs by KEY SHAPE: any key at the top level, or inside
 * `$set`/`$set_once`, whose lowercased name ends in `url`, `pathname` or
 * `referrer` gets its string value replaced. This also covers whichever of
 * top-level vs. nested-under-$set_once shape posthog-js happens to use for
 * a given call (the installed build uses both, depending on which internal
 * path produces the property).
 *
 * "Replaced" means de-slugged, not erased (BIL-5257): a pathname becomes
 * the matched route's pattern (`/acme/proj-9/machine/482913` →
 * `/machine/:id`) and a URL becomes origin + basename + that pattern, so
 * PostHog's URL-based reports (Web Analytics → Paths) show screens while
 * still carrying no slug or id. Unmatched paths collapse to `/`. Referrers
 * are reduced to their origin — an external referring domain is useful,
 * its path is not ours to interpret.
 *
 * Pure, synchronous, never throws — a thrown error here must not break
 * event capture, so any failure returns the input `properties` unchanged.
 */

const URL_LIKE_SUFFIXES = ['url', 'pathname', 'referrer'] as const;
type UrlLikeSuffix = (typeof URL_LIKE_SUFFIXES)[number];

const matchUrlLikeSuffix = (key: string): UrlLikeSuffix | undefined => {
  const lower = key.toLowerCase();
  return URL_LIKE_SUFFIXES.find((suffix) => lower.endsWith(suffix));
};

/** Origin + basename + the de-slugged route pattern (no query/hash) —
 * mirrors SentryRoute.tsx's getScrubbedCurrentUrl(), sourced from the same
 * helpers. The pattern `/` collapses to origin + basename, so a root URL
 * keeps its pre-BIL-5257 shape. */
export const buildScrubbedUrl = (pattern: string): string =>
  `${window.location.origin}${getBasename()}${pattern === '/' ? '' : pattern}`;

const getScrubbedUrl = (): string => buildScrubbedUrl('/');

/** A full URL (or a bare path) → origin + basename + its route pattern. */
const scrubUrlValue = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    return buildScrubbedUrl(resolveRoutePatternForPath(url.pathname));
  } catch {
    return getScrubbedUrl();
  }
};

/**
 * `$direct` (posthog-js's marker for "no referrer") is kept as-is. Any
 * other referrer is reduced to its origin only — external referrer
 * domains are useful and carry no path, but the path/query on a referring
 * URL can itself be a leaked company/project route.
 */
const scrubReferrerValue = (value: string): string => {
  if (value === '$direct') return value;
  try {
    return new URL(value).origin;
  } catch {
    return getScrubbedUrl();
  }
};

const scrubValueForSuffix = (
  suffix: UrlLikeSuffix,
  value: unknown
): unknown => {
  if (typeof value !== 'string') return value;
  switch (suffix) {
    case 'url':
      return scrubUrlValue(value);
    case 'pathname':
      return resolveRoutePatternForPath(value);
    case 'referrer':
      return scrubReferrerValue(value);
  }
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Scrubs URL-like keys on one flat object; returns a shallow copy. */
const scrubFlat = <T extends Record<string, unknown>>(source: T): T => {
  const result: Record<string, unknown> = { ...source };
  for (const key of Object.keys(result)) {
    const suffix = matchUrlLikeSuffix(key);
    if (!suffix) continue;
    result[key] = scrubValueForSuffix(suffix, result[key]);
  }
  return result as T;
};

export const scrubUrlProperties = (
  properties: Properties,
  _eventName: string
): Properties => {
  try {
    const scrubbed = scrubFlat(properties);

    for (const nestedKey of ['$set', '$set_once'] as const) {
      const nested = scrubbed[nestedKey];
      if (isPlainObject(nested)) {
        scrubbed[nestedKey] = scrubFlat(nested);
      }
    }

    return scrubbed;
  } catch {
    return properties;
  }
};

/**
 * posthog-js's `before_send` hook, wired up in posthog.ts. Unlike
 * `sanitize_properties` (see the note above), `before_send` receives the
 * full `CaptureResult` — a `$set`/`$set_once` a caller attaches (e.g. our
 * own identify() call, or posthog-js's own $initial_* stamping) arrives as
 * TOP-LEVEL `cr.$set` / `cr.$set_once` fields, not nested inside
 * `cr.properties`. So this scrubs all three bags — `cr.properties`,
 * `cr.$set`, `cr.$set_once` — by delegating each to scrubUrlProperties(),
 * which also handles the (harmless, defensive) case of $set/$set_once
 * arriving nested inside one of those bags instead.
 *
 * `before_send` returning `null` drops the event entirely — this must
 * never drop an event, only scrub it, so `null` only propagates when the
 * input itself was `null`. Never throws: any failure returns the input
 * `cr` unchanged.
 */
export const scrubCaptureResult = (
  cr: CaptureResult | null
): CaptureResult | null => {
  if (cr === null) return null;
  try {
    const scrubbed: CaptureResult = {
      ...cr,
      properties: scrubUrlProperties(cr.properties, cr.event),
    };
    if (cr.$set !== undefined) {
      scrubbed.$set = scrubUrlProperties(cr.$set, cr.event);
    }
    if (cr.$set_once !== undefined) {
      scrubbed.$set_once = scrubUrlProperties(cr.$set_once, cr.event);
    }
    return scrubbed;
  } catch {
    return cr;
  }
};
